import { GridApi, GridOptions } from 'ag-grid-community';
import React, {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { useTaskDrawer } from 'components/organisms/task-drawer';
import { useOnGridReady } from './use-on-grid-ready';

interface UseOnTaskDrawerToggleParams {
  onShow: (taskId: number) => void;
  onHide: (taskId: number) => void;
  enable: boolean;
}

const useOnTaskDrawerToggle = ({
  onShow,
  onHide,
  enable,
}: UseOnTaskDrawerToggleParams) => {
  const [params] = useTaskDrawer();
  const [state, , [, taskId] = []] = params;
  const alreadyHighlighted = useRef<number>();

  useEffect(() => {
    if (!enable) {
      return;
    }

    if (state === 'show') {
      if (alreadyHighlighted.current) {
        onHide(alreadyHighlighted.current);
      }
      onShow(taskId!);
      alreadyHighlighted.current = taskId;
    } else if (state === 'invalid' && alreadyHighlighted.current) {
      onHide(alreadyHighlighted.current);
    }
  }, [onHide, onShow, state, taskId, enable]);
};

const updateRowNodeToHighlight = ({
  shouldHighlight,
  taskId,
  apiRef,
}: {
  shouldHighlight: boolean;
  taskId: number;
  apiRef: GridApi | null;
}) => {
  const rowNode = apiRef?.getRowNode(String(taskId));

  const isHighlightTakenAsField =
    typeof rowNode?.data.highlighted === 'boolean' ||
    rowNode?.data.highlighted === undefined;

  if (rowNode && isHighlightTakenAsField) {
    rowNode?.setData({ ...rowNode.data, highlighted: shouldHighlight });
  }
};

export const withHighlightOnTaskDrawer = <
  T extends Pick<GridOptions, 'onGridReady'>,
>(
  Component: ComponentType<T>,
): FC<T> => {
  const Next: FC<T> = ({ onGridReady: onReady, ...props }) => {
    const [apiRef, , onGridReady] = useOnGridReady(onReady);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
      const api = apiRef.current;
      const onViewPortChanged = () => {
        setIsInitialized(true);
      };

      api?.addEventListener('viewportChanged', onViewPortChanged);

      return () =>
        api?.removeEventListener('viewportChanged', onViewPortChanged);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiRef.current]);

    useOnTaskDrawerToggle({
      onShow: useCallback(
        (taskId) =>
          updateRowNodeToHighlight({
            shouldHighlight: true,
            taskId,
            apiRef: apiRef.current,
          }),
        [apiRef],
      ),
      onHide: useCallback(
        (taskId) =>
          updateRowNodeToHighlight({
            apiRef: apiRef.current,
            shouldHighlight: false,
            taskId,
          }),
        [apiRef],
      ),
      enable: isInitialized,
    });

    return (
      <Component
        {...(props as any)}
        onGridReady={onGridReady}
        rowClassRules={{
          highlighted: (params) => {
            return params?.node?.data?.highlighted ?? false;
          },
        }}
      />
    );
  };

  return setDisplayName(wrapDisplayName(Component, 'WithSelections'))(Next);
};
